/**
 * TenantSettings — view/edit your white-label tenant.
 *
 * Shows trial countdown if active, plan info, and lets the owner edit brand
 * name / logo / accent color.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Save, Image as ImageIcon, X, Clock, Crown, CreditCard, UserPlus, Send, Copy, Trash2, ArrowRightLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useTenantSocket from '@/lib/useTenantSocket';

const PALETTE = ['#8B3FC5', '#0A4D8C', '#0F766E', '#7C3AED', '#DC2626', '#0A0A0A', '#475569', '#06B6D4'];
const INVITE_ROLES = [
  { id: 'holding_admin', label: 'Admin' },
  { id: 'legal', label: 'Legal' },
  { id: 'cfo', label: 'CFO' },
  { id: 'ceo', label: 'CEO' },
  { id: 'chairman', label: 'Chairman' },
  { id: 'investor', label: 'Investor' },
  { id: 'founder', label: 'Founder' },
  { id: 'guest', label: 'Guest' },
];

export default function TenantSettings() {
  const [t, setT] = useState(null);
  const [form, setForm] = useState({ brand_name: '', primary_color: '#8B3FC5', logo_url: '' });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/tenants/me');
      setT(data);
      setForm({
        brand_name: data.brand_name || '',
        primary_color: data.primary_color || '#8B3FC5',
        logo_url: data.logo_url || '',
      });
    } catch (err) {
      console.error('TenantSettings: load failed', err);
      toast.error('Could not load tenant.');
    }
  }, []);
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect */
  useEffect(() => { load(); }, [load]);
  /* eslint-enable */

  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    fd.append('kind', 'tenant_logo');
    setBusy(true);
    try {
      const { data } = await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((x) => ({ ...x, logo_url: data.url || data.public_url || data.download_url || '' }));
      toast.success('Logo uploaded.');
    } catch (err) {
      console.error('TenantSettings: logo upload failed', err);
      toast.error('Upload failed.');
    }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/tenants/me', form);
      toast.success('Brand updated.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/billing/portal');
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not open billing portal.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No active subscription — visit pricing first.');
    } finally { setBusy(false); }
  };

  // ------- Members / Invites -------
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'guest', name: '', note: '' });
  const loadInvites = useCallback(async () => {
    try {
      const { data } = await api.get('/tenants/invites');
      setInvites(data || []);
    } catch (err) {
      // Non-admin members hit this — expected. Log for debugging only.
      if (err?.response?.status && err.response.status !== 403) {
        console.error('TenantSettings: loadInvites failed', err);
      }
    }
  }, []);
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect */
  useEffect(() => { loadInvites(); }, [loadInvites]);
  /* eslint-enable */

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) { toast.error('Email required.'); return; }
    setBusy(true);
    try {
      await api.post('/tenants/invite', inviteForm);
      toast.success(`Invite sent to ${inviteForm.email}.`);
      setInviteForm({ email: '', role: 'guest', name: '', note: '' });
      loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send invite.');
    } finally { setBusy(false); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm('Revoke this invitation?')) return;
    try {
      await api.delete(`/tenants/invite/${id}`);
      toast.success('Invitation revoked.');
      loadInvites();
    } catch { toast.error('Revoke failed.'); }
  };

  const resendInvite = async (id) => {
    try {
      const { data } = await api.post(`/tenants/invite/${id}/resend`);
      toast.success(
        data.email_sent
          ? `Reminder email sent to ${data.email}.`
          : `Invite refreshed — SMTP off, copy the link manually.`,
      );
      loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed.');
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied.');
  };

  if (!t) return <div className="text-sm text-slate-500">Loading tenant…</div>;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Tenant Settings</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            {t.is_primary ? 'Tenant settings.' : 'Your white-label workspace.'}
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            {t.is_primary
              ? 'You are on the primary Vigilant Entities Holding tenant — branding is managed at the platform level.'
              : 'Customise how your team sees Vigilant — brand name, accent colour, and logo.'}
          </p>
        </div>
        {!t.is_primary && (
          <button onClick={save} disabled={busy} data-testid="tenant-save" className="ve-btn-primary self-start">
            <Save size={14}/> {busy ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>

      {/* Trial banner */}
      {t.trial_days_left != null && (
        <div className="mt-6 ve-card p-5 border-[#8B3FC5] bg-gradient-to-r from-[#FFF8E5] to-white flex items-center justify-between" data-testid="trial-banner">
          <div className="flex items-center gap-4">
            <span className="h-11 w-11 rounded-md bg-[#8B3FC5]/15 text-[#5E1F8B] flex items-center justify-center">
              <Clock size={18}/>
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5E1F8B] font-bold">7-day trial</div>
              <div className="display text-lg font-bold mt-1">{t.trial_days_left} day{t.trial_days_left === 1 ? '' : 's'} remaining</div>
            </div>
          </div>
          <Link to="/pricing" className="ve-btn-primary text-xs"><CreditCard size={12}/> Upgrade</Link>
        </div>
      )}

      {/* Billing & subscription section — always shown */}
      <div className="mt-6 ve-card p-5 flex items-center justify-between" data-testid="billing-section">
        <div className="flex items-center gap-4">
          <span className="h-11 w-11 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <CreditCard size={18}/>
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold">Subscription &amp; billing</div>
            <div className="display text-lg font-bold mt-1">
              {t.plan === 'annual' ? 'Annual — QAR 75,000 / year' :
                t.plan === 'monthly' ? 'Monthly — QAR 10,000 / month' :
                  'Trial / not yet subscribed'}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Manage card, download receipts, or cancel via the Stripe portal.
            </div>
          </div>
        </div>
        <button
          onClick={openPortal}
          disabled={busy}
          data-testid="open-billing-portal"
          className="ve-btn-secondary text-xs"
        >
          {busy ? 'Opening…' : 'Manage billing'}
        </button>
      </div>

      {t.is_primary ? (
        <div className="mt-8 ve-card p-8 text-center">
          <Crown size={32} className="mx-auto text-[#5E1F8B]"/>
          <h3 className="display text-2xl mt-3 font-bold">You&apos;re on the primary tenant.</h3>
          <p className="text-sm text-slate-600 mt-2">This is the Vigilant Entities Holding workspace — branding is managed at the platform level.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="ve-card p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Brand identity</div>
            <label className="block mt-5 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Brand name
              <input
                value={form.brand_name}
                onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                data-testid="tenant-brand-name"
                className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"
              />
            </label>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">Accent colour</div>
              <div className="flex flex-wrap gap-2">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    onClick={() => setForm({ ...form, primary_color: c })}
                    data-testid={`tenant-color-${c.slice(1)}`}
                    className={`h-9 w-9 rounded-full border-2 transition-all ${form.primary_color === c ? 'border-[#0A0A0A] scale-110' : 'border-white hover:scale-105'}`}
                    style={{ background: c, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="ve-card p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Logo</div>
            <div
              onClick={() => fileRef.current?.click()}
              className="mt-5 rounded-lg border-2 border-dashed border-slate-200 hover:border-[#8B3FC5] cursor-pointer p-8 text-center transition-colors"
            >
              {form.logo_url ? (
                <div className="relative inline-block">
                  <img src={form.logo_url} alt="logo" className="h-16 mx-auto object-contain"/>
                  <button
                    onClick={(e) => { e.stopPropagation(); setForm({ ...form, logo_url: '' }); }}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-500 text-white text-xs hover:bg-rose-600"
                  ><X size={12} className="mx-auto"/></button>
                </div>
              ) : (
                <>
                  <ImageIcon size={28} className="mx-auto text-slate-300"/>
                  <p className="mt-3 text-sm text-slate-600">Click to upload your logo</p>
                </>
              )}
              <input
                ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="tenant-logo-file"
              />
            </div>
            {/* Preview */}
            <div className="mt-5 rounded-lg border border-slate-200 p-4 bg-white flex items-center gap-3">
              {form.logo_url ? <img src={form.logo_url} className="h-10 w-10 object-contain rounded"/> : <div className="h-10 w-10 rounded" style={{ background: form.primary_color }}/>}
              <div>
                <div className="display text-lg font-bold" style={{ color: form.primary_color }}>{form.brand_name || 'Your brand'}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-0.5">Powered by Vigilant</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reporting currency — Amwāl OS default for this tenant.
          Works for primary AND white-label tenants alike (separate endpoint
          that upserts the doc, bypassing the brand-edit gate). */}
      <FinanceCurrencyCard tenant={t} onSaved={load} />

      {/* Members / Invites */}
      <div className="mt-8 ve-card p-6" data-testid="invites-section">
        <div className="flex items-center justify-between">
          <div>
            <div className="ve-eyebrow">Team & Invitations</div>
            <h3 className="display text-xl font-bold mt-2">Invite teammates to your tenant.</h3>
            <p className="text-sm text-slate-600 mt-1">They&apos;ll get an email + a 7-day acceptance link. New members join with the role you choose.</p>
          </div>
        </div>

        <form onSubmit={sendInvite} className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="invite-form">
          <input
            type="email" required placeholder="teammate@company.com"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            data-testid="invite-email-input"
            className="md:col-span-5 rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"
          />
          <input
            placeholder="Name (optional)"
            value={inviteForm.name}
            onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
            data-testid="invite-name-input"
            className="md:col-span-3 rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"
          />
          <select
            value={inviteForm.role}
            onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
            data-testid="invite-role-select"
            className="md:col-span-2 rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"
          >
            {INVITE_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <button
            type="submit" disabled={busy}
            data-testid="invite-send-btn"
            className="md:col-span-2 ve-btn-primary justify-center"
          >
            <Send size={12}/> {busy ? 'Sending…' : 'Invite'}
          </button>
          <input
            placeholder="Personal note (optional)"
            value={inviteForm.note}
            onChange={(e) => setInviteForm({ ...inviteForm, note: e.target.value })}
            data-testid="invite-note-input"
            className="md:col-span-12 rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"
          />
        </form>

        {/* Invites list */}
        {invites.length > 0 && (
          <div className="mt-7 border-t border-slate-200 pt-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
              Recent invitations
            </div>
            <div className="space-y-2">
              {invites.slice(0, 10).map((inv) => (
                <div key={inv.invite_id} data-testid={`invite-row-${inv.invite_id}`}
                     className="flex items-center justify-between gap-3 rounded-md border border-slate-100 px-3 py-2.5">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <UserPlus size={14} className="text-slate-400 shrink-0"/>
                    <div className="min-w-0">
                      <div className="text-sm font-bold truncate">{inv.email}</div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-0.5">
                        <span className="uppercase tracking-[0.15em]">{inv.role}</span>
                        <span>·</span>
                        <span className={`uppercase tracking-[0.15em] font-bold ${
                          inv.status === 'accepted' ? 'text-emerald-600' :
                            inv.status === 'revoked' ? 'text-rose-600' : 'text-amber-600'
                        }`}>{inv.status}</span>
                      </div>
                    </div>
                  </div>
                  {inv.status === 'pending' && (
                    <>
                      <button
                        onClick={() => resendInvite(inv.invite_id)}
                        title="Resend"
                        data-testid={`invite-resend-${inv.invite_id}`}
                        className="p-2 text-slate-400 hover:text-[#5E1F8B]"
                      ><Send size={14}/></button>
                      <button
                        onClick={() => copyLink(`${window.location.origin}/invite/${inv.token}`)}
                        title="Copy link"
                        className="p-2 text-slate-400 hover:text-[#5E1F8B]"
                      ><Copy size={14}/></button>
                      <button
                        onClick={() => revokeInvite(inv.invite_id)}
                        title="Revoke"
                        className="p-2 text-slate-400 hover:text-rose-600"
                      ><Trash2 size={14}/></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Reporting-currency card — sets the Amwāl OS default for the tenant.
 * Visible to every tenant admin (works for primary too). Saves via the
 * dedicated `/tenants/me/reporting-currency` endpoint which upserts a
 * minimal tenants doc when one doesn't exist (primary tenant case).
 */
const SUPPORTED_REPORTING_CURRENCIES = ['QAR', 'USD', 'EUR', 'AED', 'SAR', 'GBP', 'JPY', 'CNY', 'INR'];

function FinanceCurrencyCard({ tenant, onSaved }) {
  const [picked, setPicked] = useState(tenant?.reporting_currency || 'QAR');
  const [busy, setBusy] = useState(false);
  const saved = tenant?.reporting_currency || 'QAR';
  const dirty = picked !== saved;

  // Live broadcast — if another admin flips the currency from a different
  // tab/browser, refresh the card's `saved` baseline so the dirty hint
  // disappears immediately.
  useTenantSocket((msg) => {
    if (msg?.type !== 'tenant.reporting_currency') return;
    if (msg.reporting_currency && msg.reporting_currency !== saved) {
      onSaved?.();  // re-fetch /tenants/me upstream
      // Reset the picker too if the user wasn't mid-edit
      if (!dirty) setPicked(msg.reporting_currency);
      toast.info(`Tenant default switched to ${msg.reporting_currency}${msg.changed_by ? ` by ${msg.changed_by}` : ''}.`);
    }
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/tenants/me/reporting-currency', { reporting_currency: picked });
      toast.success(`${picked} is now your tenant's default reporting currency.`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 ve-card p-6" data-testid="tenant-reporting-currency-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="ve-eyebrow flex items-center gap-2">
            <ArrowRightLeft size={11} />
            Amwāl OS — Reporting currency
          </div>
          <h3 className="display text-xl font-bold mt-2">Tenant-wide default for financial rollups.</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Every member of this tenant lands on the Financial Engine with this currency pre-selected.
            They can still override locally — this is just the starting point. FX rates apply.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            data-testid="tenant-reporting-currency-select"
            className="px-3 py-2 text-sm font-semibold border border-slate-200 rounded-md focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"
          >
            {SUPPORTED_REPORTING_CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button
            onClick={save}
            disabled={!dirty || busy}
            data-testid="tenant-reporting-currency-save"
            className="ve-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={14} /> {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {dirty && (
        <div className="mt-3 text-xs text-[#5E1F8B]">
          Unsaved change — currently <strong>{saved}</strong>, will become <strong>{picked}</strong>.
        </div>
      )}
    </div>
  );
}
